import { action, computed, makeObservable, observable, when } from 'mobx';

import { type GEO_RESTRICTION_DIALOG_TYPE, SUPPORTED_GEO_RESTRICTION_TYPES } from '../../features/geography';
import {
  takeGpsResultPending,
  trackGpsResultFailure,
  trackGpsResultSuccess,
  trackLocationNoticeImpression,
} from '../../features/geography/analytics';
import { postRequest } from '../../services/api';
import { resolveFlag } from '../../services/launchDarkly';
import { getErrorMessage } from '../../utils/errors';
import { getUserLocation } from '../../utils/getUserLocation';
import type { RootStore } from '../RootStore';
import { EnumOpenDialog } from '../ui/types';

export class GeoRestrictionStore {
  private readonly rootStore: RootStore;

  @observable
  private _popUpShown = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this);

    // The profile of the boot after an ENABLE GPS reload carries the restriction resolved with GPS coords.
    when(
      () => !!this.rootStore.userStore.profile,
      () => void this.reportPendingGpsResult(),
    );
  }

  private async reportPendingGpsResult(): Promise<void> {
    if (!takeGpsResultPending()) {
      return;
    }
    // Permission was granted before the reload, but the position itself can still fail (device location off,
    // no fix in time) — then the server fell back to IP and the restriction isn't GPS-confirmed.
    const position = await getUserLocation();
    if (!position) {
      trackGpsResultFailure('gps_unavailable', 'GPS position unavailable after permission was granted');
      return;
    }
    trackGpsResultSuccess(this.geoRestrictionModalType);
  }

  @computed
  public get geoRestrictionModalType(): GEO_RESTRICTION_DIALOG_TYPE | undefined {
    const type = this.rootStore.userStore.profile?.geoRestrictionMetadata?.geoRestrictionType;
    if (!type) {
      return undefined;
    }
    if (SUPPORTED_GEO_RESTRICTION_TYPES.has(type)) {
      return type as GEO_RESTRICTION_DIALOG_TYPE;
    }
    return undefined;
  }

  @computed
  public get shouldShowGeoRestriction(): boolean {
    const flagEnabled = resolveFlag('geo_restriction_pop_up', false);
    if (this._popUpShown || !flagEnabled) {
      return false;
    }
    return !!this.geoRestrictionModalType;
  }

  @action
  public showGeoRestrictionPopUp(): void {
    const type = this.geoRestrictionModalType;
    if (!type) {
      return;
    }
    this._popUpShown = true;
    trackLocationNoticeImpression(type, this.rootStore.userStore.profile?.signInCount === 0 ? 'signup' : 'login');
    this.rootStore.uiStore.setOpenDialog({
      dialog: EnumOpenDialog.isOpenGeoRestrictionDialog,
      options: { type },
    });
  }

  @action
  public acknowledgeGeoRestriction(): void {
    this.rootStore.uiStore.setCloseDialog();
    // No retry: without a recorded acknowledge the server keeps the notice due, so it shows again next boot.
    void postRequest('geography/restriction-popup/acknowledge').catch((error) => {
      console.error(`Failed to acknowledge geo restriction pop-up: ${getErrorMessage(error)}`);
    });
  }

  @action
  public clean(): void {
    this._popUpShown = false;
  }
}
